import { CalendarCheck, CalendarClock, Hammer, Lock } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@buildpy/ui/alert-dialog";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Radio } from "@buildpy/ui/radio";
import { SimpleSelect } from "@buildpy/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { settleRedirect } from "@/lib/redirect-error";
import {
  closeProject,
  completeProject,
  rescheduleProject,
  setProjectAssignee,
  setProjectWarrantyEnd,
  startProject,
} from "@/api/actions/project";
import { warrantyEndDate } from "@buildpy/core/modules/project/build-closeout";
import { warrantyEndMax } from "@buildpy/core/modules/project/transitions";
import type { ProjectCompleteNext } from "@buildpy/core/modules/project/types";

interface MemberOption {
  id: string;
  name: string;
}

interface Props {
  projectId: string;
  rev: number;
  // 버튼을 보일지는 서버가 전이표로 정해 넘긴다.
  canStart: boolean;
  canReschedule: boolean;
  plannedStartDate: string | null;
  assigneeId: string | null;
  members: readonly MemberOption[];
  // 준공 — 시공중이면 준공 처리, 유지보수면 현장 닫기. 날짜는 착공일(KST)~오늘. 주 버튼은 「오늘 기록」 이라 둘 다 보조 버튼이다.
  canComplete: boolean;
  canClose: boolean;
  completionMin: string | null;
  today: string;
  defaultNext: ProjectCompleteNext;
  warrantyMonths: number;
  // 유지보수 종료일 변경 — 유지보수 현장만, 준공일 이후.
  canSetWarrantyEnd: boolean;
  completedOn: string | null;
  warrantyEnd: string | null;
  // 완료된 현장 — 담당자도 못 바꾼다.
  readOnly: boolean;
}

// 현장 상세의 액션 바 — 착공 · 착공일 변경 · 담당자 · 준공 · 현장 닫기 · 유지보수 종료일 변경.
const ProjectActions = ({
  projectId,
  rev,
  canStart,
  canReschedule,
  plannedStartDate,
  assigneeId,
  members,
  canComplete,
  canClose,
  completionMin,
  today,
  defaultNext,
  warrantyMonths,
  canSetWarrantyEnd,
  completedOn,
  warrantyEnd,
  readOnly,
}: Props) => {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [startDate, setStartDate] = useState(plannedStartDate ?? "");
  const [completeOpen, setCompleteOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [completionDate, setCompletionDate] = useState(today);
  const [next, setNext] = useState<ProjectCompleteNext>(defaultNext);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const [warrantyEndInput, setWarrantyEndInput] = useState(warrantyEnd ?? "");

  const run = useCallback(
    async (fallback: string, call: () => Promise<{ ok: boolean; message?: string }>, done: () => void) => {
      setPending(true);
      try {
        const result = await call();
        if (!result.ok) {
          toast.error(result.message ?? fallback);
          return;
        }
        done();
        router.refresh();
      } catch (error) {
        if (settleRedirect(error)) return;
        toast.error(fallback);
      } finally {
        setPending(false);
      }
    },
    [router]
  );

  const doStart = () =>
    void run(
      "착공하지 못했습니다",
      () => startProject(projectId, { base_rev: rev }),
      () => {
        toast.success("착공했습니다");
        setStartOpen(false);
      }
    );

  const doReschedule = () =>
    void run(
      "착공일을 바꾸지 못했습니다",
      async () => {
        const result = await rescheduleProject(projectId, { start_date: startDate, base_rev: rev });
        if (result.ok && result.ends_after_contract) {
          toast.warning("준공 예정일이 계약서의 준공일을 넘깁니다 — 고객과 일정을 다시 맞춰 주세요");
        }
        return result;
      },
      () => {
        toast.success("착공일을 바꿨습니다");
        setRescheduleOpen(false);
      }
    );

  // 끝나지 않은 공정이 있으면 서버가 needs_confirm 으로 멈추고, 확인하면 force 로 다시 보낸다.
  const doComplete = (force: boolean) => {
    let asked = false;
    void run(
      "준공하지 못했습니다",
      async () => {
        const input = { completion_date: completionDate, next, base_rev: rev };
        const result = await completeProject(projectId, input, force);
        if (!result.ok && !force && result.needs_confirm) {
          asked = true;
          setConfirmMessage(result.message);
          return { ok: true };
        }
        return result;
      },
      () => {
        if (asked) return;
        toast.success(next === "WARRANTY" ? "준공했습니다 — 유지보수 기간이 시작됩니다" : "준공했습니다");
        setConfirmMessage(null);
        setCompleteOpen(false);
      }
    );
  };

  const doClose = () =>
    void run(
      "현장을 닫지 못했습니다",
      () => closeProject(projectId, { base_rev: rev }),
      () => {
        toast.success("현장을 닫았습니다");
        setCloseOpen(false);
      }
    );

  const doWarrantyEnd = () =>
    void run(
      "유지보수 종료일을 바꾸지 못했습니다",
      () => setProjectWarrantyEnd(projectId, { warranty_end_date: warrantyEndInput, base_rev: rev }),
      () => {
        toast.success("유지보수 종료일을 바꿨습니다");
        setWarrantyOpen(false);
      }
    );

  const changeAssignee = (value: string) =>
    void run(
      "담당자를 바꾸지 못했습니다",
      () => setProjectAssignee(projectId, { assignee_id: value || null, base_rev: rev }),
      () => toast.success(value ? "담당자를 바꿨습니다" : "담당자를 비웠습니다")
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {readOnly && (
        <span className="flex items-center gap-x-1 text-sm text-muted-foreground" data-testid="project-readonly">
          <Lock className="size-3.5" />
          읽기 전용
        </span>
      )}
      {!readOnly && (
        <label className="flex items-center gap-x-2 text-sm">
          <span className="text-muted-foreground">담당자</span>
          <SimpleSelect
            aria-label="담당자"
            className="w-auto min-w-28"
            disabled={pending}
            options={[{ value: "", label: "미지정" }, ...members.map((m) => ({ value: m.id, label: m.name }))]}
            size="sm"
            value={assigneeId ?? ""}
            onValueChange={changeAssignee}
          />
        </label>
      )}

      {canReschedule && (
        <Button disabled={pending} size="sm" variant="outline" onClick={() => setRescheduleOpen(true)}>
          <CalendarClock />
          착공일 변경
        </Button>
      )}

      {canStart && (
        <Button disabled={pending} size="sm" variant="primary" onClick={() => setStartOpen(true)}>
          <Hammer />
          착공
        </Button>
      )}

      {canComplete && (
        <Button
          data-testid="project-complete-open"
          disabled={pending}
          size="sm"
          variant="outline"
          onClick={() => {
            setCompletionDate(today);
            setNext(defaultNext);
            setCompleteOpen(true);
          }}>
          <CalendarCheck />
          준공 처리
        </Button>
      )}

      {canClose && (
        <Button
          data-testid="project-close-open"
          disabled={pending}
          size="sm"
          variant="outline"
          onClick={() => setCloseOpen(true)}>
          <Lock />
          현장 닫기
        </Button>
      )}

      {canSetWarrantyEnd && (
        <Button
          data-testid="project-warranty-end-open"
          disabled={pending}
          size="sm"
          variant="outline"
          onClick={() => {
            setWarrantyEndInput(warrantyEnd ?? "");
            setWarrantyOpen(true);
          }}>
          <CalendarClock />
          유지보수 종료일 변경
        </Button>
      )}

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>준공 처리</DialogTitle>
            <DialogDescription>시공을 마칩니다. 준공한 뒤에는 공정을 더 고칠 수 없습니다. 유지보수 중에는 일지·사진(무상 A/S 포함)을 계속 남길 수 있고, 바로 완료하면 기록도 닫힙니다.</DialogDescription>
          </DialogHeader>
          <Field label="준공일">
            <Input
              aria-label="준공일"
              data-testid="project-completion-date"
              inputSize="sm"
              max={today}
              min={completionMin ?? undefined}
              name="completion_date"
              type="date"
              value={completionDate}
              onChange={(e) => setCompletionDate(e.target.value)}
            />
          </Field>
          <fieldset className="flex flex-col gap-y-1.5">
            <legend className="mb-1 text-xs text-muted-foreground">다음 상태</legend>
            <label className="flex items-center gap-x-2 text-sm">
              <Radio
                aria-label={`유지보수 ${warrantyMonths}개월`}
                checked={next === "WARRANTY"}
                data-testid="project-next-warranty"
                name="project-complete-next"
                value="WARRANTY"
                onChange={() => setNext("WARRANTY")}
              />
              <span>유지보수 {warrantyMonths}개월</span>
              {next === "WARRANTY" && completionDate && (
                <span className="tabular-nums text-muted-foreground">
                  ~{warrantyEndDate(completionDate, warrantyMonths)}
                </span>
              )}
            </label>
            <label className="flex items-center gap-x-2 text-sm">
              <Radio
                aria-label="바로 완료"
                checked={next === "CLOSED"}
                data-testid="project-next-closed"
                name="project-complete-next"
                value="CLOSED"
                onChange={() => setNext("CLOSED")}
              />
              <span>바로 완료</span>
            </label>
          </fieldset>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setCompleteOpen(false)}>
              취소
            </Button>
            <Button
              data-testid="project-complete-submit"
              disabled={pending || !completionDate}
              size="sm"
              variant="primary"
              onClick={() => doComplete(false)}>
              준공
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmMessage !== null} onOpenChange={(open) => !open && setConfirmMessage(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>그래도 준공할까요?</AlertDialogTitle>
            <AlertDialogDescription>{confirmMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              data-testid="project-complete-force"
              disabled={pending}
              onClick={(e) => {
                e.preventDefault();
                doComplete(true);
              }}>
              준공
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>현장 닫기</DialogTitle>
            <DialogDescription>
              유지보수를 마치고 현장을 완료로 닫습니다. 닫은 현장은 읽기 전용이 됩니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setCloseOpen(false)}>
              취소
            </Button>
            <Button data-testid="project-close-submit" disabled={pending} size="sm" variant="primary" onClick={doClose}>
              닫기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={warrantyOpen} onOpenChange={setWarrantyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>유지보수 종료일 변경</DialogTitle>
            <DialogDescription>
              준공일 {completedOn ?? "미정"} · 현재 종료일 {warrantyEnd ?? "미정"}. 종료일은 준공일 이후여야 합니다.
            </DialogDescription>
          </DialogHeader>
          <Field label="새 종료일">
            <Input
              data-testid="project-warranty-end-date"
              inputSize="sm"
              max={completedOn ? warrantyEndMax(completedOn) : undefined}
              min={completedOn ?? undefined}
              name="warranty_end_date"
              type="date"
              value={warrantyEndInput}
              onChange={(e) => setWarrantyEndInput(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setWarrantyOpen(false)}>
              취소
            </Button>
            <Button
              data-testid="project-warranty-end-submit"
              disabled={pending || !warrantyEndInput}
              size="sm"
              variant="primary"
              onClick={doWarrantyEnd}>
              바꾸기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>착공</DialogTitle>
            <DialogDescription>현장을 시공중으로 바꿉니다. 착공한 뒤에는 착공일을 바꿀 수 없습니다.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setStartOpen(false)}>
              취소
            </Button>
            <Button disabled={pending} size="sm" variant="primary" onClick={doStart}>
              착공
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>착공일 변경</DialogTitle>
            <DialogDescription>
              모든 공정을 새 착공일로 다시 펼칩니다. 계약서의 착공일은 그대로입니다.
            </DialogDescription>
          </DialogHeader>
          <Field label="새 착공일">
            <Input
              inputSize="sm"
              name="start_date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setRescheduleOpen(false)}>
              취소
            </Button>
            <Button disabled={pending || !startDate} size="sm" variant="primary" onClick={doReschedule}>
              바꾸기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectActions;
